import './Page.scss';

import {
  ColDef,
  ColGroupDef,
  GetRowIdParams,
  GridApi,
  GridOptions,
  RowDragEndEvent,
  RowDragEnterEvent,
} from '@amzn/gft-ag-grid-enterprise';
import { GFTSearchInput } from '@amzn/gft-frontend-commons';
import AurigaLoader from '@components/Common/AurigaLoader';
import { KPI_DIMENSION_CONFIG_MAP, KPI_NAME_INDENT_SPACES } from '@constants/page';
import { useAurigaContext } from '@context/AurigaContext';
import { useDeckContext } from '@context/DeckContext';
import { useDecklinesContext } from '@context/DecklinesContext';
import { useFootnotesContext } from '@context/FootnotesContext.tsx';
import { useKPILibraryContext } from '@context/KPILibraryContext';
import { usePageContext } from '@context/PageContext';
import { PermissionLevel, useUserContext } from '@context/UserContext.tsx';
import AuditHistoryDialog from '@deckManagement/components/AuditHistory/AuditHistoryDialog';
import CommentsDrawer from '@deckManagement/components/Comments/CommentsDrawer';
import useCommentsDrawer from '@deckManagement/components/Comments/hooks/useCommentsDrawer';
import AsyncSaveButton from '@deckManagement/components/Common/AsyncSaveButton/AsyncSaveButton';
import { splitRawFormula } from '@deckManagement/components/Common/CalculatedKPIBuilder/utils/formulaHelpers';
import LockPageButton from '@deckManagement/components/Common/LockPageButton/LockPageButton';
import KpiNameCellRenderer from '@deckManagement/components/Page/CellRenderers/KpiNameCellRenderer';
import DisplayNameDialog from '@deckManagement/components/Page/DisplayNameDialog/DisplayNameDialog';
import useDisplayNameDialog from '@deckManagement/components/Page/DisplayNameDialog/useDisplayNameDialog';
import FootnotesModal from '@deckManagement/components/Page/Footnotes/FootnotesModal';
import FootnotesSection from '@deckManagement/components/Page/Footnotes/FootnotesSection';
import FootnoteHeaderRenderer, {
  type FootnoteHeaderParams,
} from '@deckManagement/components/Page/HeaderComponents/FootnoteHeaderRenderer';
import { getSectionBlockLeafCounts } from '@deckManagement/components/Page/hooks/useHeaderTreeColumns';
import SectionGrid from '@deckManagement/components/Page/SectionGrid/SectionGrid';
import usePageGridManager, { type GridManager } from '@deckManagement/hooks/usePageGridManager';
import useDecklineGridStyling from '@hooks/useDecklineGridStyling';
import useEditDurationTracker from '@hooks/useEditDurationTracker';
import useRecordLoadComplete from '@hooks/useRecordLoadComplete';
import { useSharedExcelExport } from '@hooks/useSharedExcelExport';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DownloadIcon from '@mui/icons-material/Download';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { createId } from '@paralleldrive/cuid2';
import {
  CellFormat,
  CodeOrDescription,
  DatasetSource,
  type DecklineCellFormat,
  PageScaleValues,
} from '@services/api/deckManagement/graphql/schema/API';
import { useGetPageLayout } from '@services/api/deckManagement/hooks/useGetPageLayout';
import { toAurigaPageData, XLSX_MIME_TYPE } from '@utils/aurigaExportData';
import { entityKeyAsId } from '@utils/deckHelpers';
import { determineDecklineType } from '@utils/decklineHelpers';
import { downloadBlob, type ExcelExportFormatSection, processExcelExport } from '@utils/excelExportHelpers';
import { formatFootnoteSuperscripts } from '@utils/footnoteHelpers';
import { cellFormatOverrideFor, pageScaleDivisor, resolveCellFormat } from '@utils/formatResolver';
import {
  asGalaxyDrivers,
  datasetSourceFromDrivers,
  getEffectiveDrivers,
  readPageDrivers,
} from '@utils/kpiDriverHelpers';
import { pageDecklineContextMenuOptions, pageDecklineDevAdminContextMenuOptions } from '@utils/pageHelpers';
import { every, filter, forEach, includes, isEmpty, isNil } from 'lodash';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EnhancedDeckline, NewDecklinePosition } from 'types/DecklinesContext.types';
import {
  ColumnLeaf,
  ContextMenuEnums,
  DecklineContextMenuOptions,
  DecklineRowData,
  PageGranularity,
  PageLayoutSection,
} from 'types/Page.types';
import { AnyKPIDrivers, KpiDimension, KpiDriverValues, PageSidePanelDropDown } from 'types/PageSidePanel.types';
import useResizeObserver from 'use-resize-observer';

// The max age (in days) for a Comment item to be displayed.
const COMMENT_MAX_AGE_DAYS = 60;

// Sets the minimum number of characters needed for Search. Can adjust as needed.
const MINIMUM_SEARCH_TERM_LENGTH = 2;

// Pixel buffer so rendered Grid does not overlap even in the case of additions like System Alerts
const GRID_HEIGHT_BUFFER = 32;

// Page-owned title groups are emitted separately through prependContent for Excel export.
const EXCEL_PREPEND_TITLE_HEADER_CLASSES = new Set([
  'header-tree-report-title',
  'header-tree-report-grain',
  'header-tree-report-unit',
]);

/**
 * Formats a DimSum Node Description for display purposes (i.e.: removes "[Weight: 1]")
 * @param description
 */
const formatNodeDescription = (description: string) => {
  return description.replace(/\[Weight.*?]$/, '').trim();
};

/**
 * Page Component that serves the needs of rendering an AGGrid based on associated Template information
 */
const Page = () => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { toastNotification, featureFlags, renderAlerts } = useAurigaContext();
  const sharedExcelExport = useSharedExcelExport();
  const { hasPermissionLevel } = useUserContext();
  const isDevTeamAdmin = hasPermissionLevel(PermissionLevel.DEV_TEAM_ADMIN);
  const { currentDeckId } = useDeckContext();
  const {
    availablePageGrains,
    currentGranularityPage,
    currentCorePageName,
    selectedGranularity,
    setSelectedGranularity,
    isLoading,
    segmentsDisplayMode,
    modifiedPageProperties,
    pageRowData,
    setPageRowData,
    pageLockStatus,
    pageLayoutOverrides,
  } = usePageContext();
  const {
    selectedDeckline,
    pageDecklines,
    resolvedNodes,
    setSubtotalMap,
    setSubtotalRanges,
    isLoading: isDecklinesLoading,
    insertPageDeckline,
    deletePageDeckline,
    handleSave,
    isSaving,
    chooseDeckline,
    repositionDeckline,
    updateDecklineComments,
    updatePageDeckline,
  } = useDecklinesContext();

  const {
    footnotes: pageFootnotes,
    isLoading: isFootnotesLoading,
    getFormattedFootnotesForExport,
    setDisplayFootnotesModal,
  } = useFootnotesContext();

  const { pendingCreations } = useKPILibraryContext();

  // Setup comments drawer for Decklines
  const commentsDrawer = useCommentsDrawer({
    key: currentGranularityPage?.sk,
    selectedItem: selectedDeckline,
    maxAgeInDays: COMMENT_MAX_AGE_DAYS,
    onSave: updateDecklineComments,
    getItemId: (deckline) => deckline.metadata.internalId,
    getItemLabel: (deckline) => deckline.metadata.actual_kpi_name ?? '',
  });

  // Display name override dialog
  const displayNameDialog = useDisplayNameDialog(selectedDeckline, updatePageDeckline);

  const { page_drivers: pageDriverSets } = modifiedPageProperties || {};

  // Edit duration tracking for RUM metrics
  const { recordSaveDuration } = useEditDurationTracker();

  // Persists current Page layout definition to local State
  // For tracking the full save lifecycle (pending creations + decklines + footnotes)
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);

  // For tracking row drag events
  const [rowDragStartIndex, setRowDragStartIndex] = useState<number | null>(null);

  // For tracking Search across the current Page
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchMatches, setSearchMatches] = useState<Set<string>>(new Set());

  // Page Grid Api manager
  // Note: Use the gridManagerRef directly when needing to use Grid manager functions
  const gridManager = usePageGridManager();
  const gridManagerRef = useRef<GridManager | null>(null);
  gridManagerRef.current = gridManager;

  // Used for dynamically managing Footnotes Section width and margins
  const { ref: containerRef, width: footnotesContainerWidth = 1 } = useResizeObserver();
  const { ref: footnotesRef, height: footnotesSectionHeight = 1 } = useResizeObserver();

  // For calculating the max Grid height (from the grid's offset within the `.main-content` scroll region)
  const gridLayoutNodeRef = useRef<HTMLDivElement | null>(null);
  // FOOTNOTES-AWARE render height (px): scroll-region height minus the grid's offset, the footnotes
  // panel height, and the buffer. Consumed ONLY by SCSS (`--max-grid-height`) as the fixed height a
  // single-section grid renders at when it scrolls, so the grid never renders under the footnotes panel.
  // = scrollContainer.clientHeight − offsetWithinContent − footnotesSectionHeight − GRID_HEIGHT_BUFFER
  const [maxGridHeight, setMaxGridHeight] = useState<number | null>(null);
  // FOOTNOTES-INDEPENDENT decision height (px): the same scroll region MINUS the grid's offset only —
  // it deliberately does NOT subtract the footnotes panel. Consumed ONLY by the autoHeight-vs-scroll
  // DECISION (a section internal-scrolls only when its content exceeds the whole scroll region), so
  // expanding/collapsing the footnotes panel can never flip a small grid into a fixed-height scroll box.
  // undefined = not yet measured (passed to SectionGrid's optional availableHeight).
  // = scrollContainer.clientHeight − offsetWithinContent − GRID_HEIGHT_BUFFER
  const [gridScrollRegionHeight, setGridScrollRegionHeight] = useState<number | undefined>(undefined);

  /**
   * Calculates the maximum height a Grid can be, from the app's scroll region and the footnotes height.
   *
   * Auriga runs FPAOneLayout in `scrollMode='content'` (shell viewport-locked), so `.main-content`
   * (owned by AurigaRoutes) is the SOLE vertical scroll container. We measure against it rather than
   * `window.innerHeight - getBoundingClientRect().top`: that rect top is viewport-relative and shrinks
   * (even goes negative) as the page scrolls, which made the budget swing with scroll position. Using
   * the scroll container's `clientHeight` (its fixed viewport, unaffected by scroll) minus the grid's
   * offset WITHIN the scroll content (the `+ scrollTop` cancels the scroll delta between the two rects)
   * yields a budget that is stable across scroll but still updates on browser resize / monitor change.
   */
  const calculateGridHeight = useCallback(() => {
    const gridLayoutElement = gridLayoutNodeRef.current;
    const scrollContainer = gridLayoutElement?.closest<HTMLElement>('.main-content');
    if (gridLayoutElement && scrollContainer) {
      const offsetWithinContent =
        gridLayoutElement.getBoundingClientRect().top -
        scrollContainer.getBoundingClientRect().top +
        scrollContainer.scrollTop;
      // Scroll region available to a grid, before footnotes. Basis for both values below. Clamped to
      // 0: on a very small viewport the raw value can go negative, which is an invalid CSS height and a
      // meaningless budget — 0 correctly means "nothing fits" (every section scrolls).
      const regionHeight = Math.max(0, scrollContainer.clientHeight - offsetWithinContent - GRID_HEIGHT_BUFFER);
      // FOOTNOTES-INDEPENDENT (decision): the whole region, so footnotes never flip small grids.
      setGridScrollRegionHeight(regionHeight);
      // FOOTNOTES-AWARE (SCSS render height): also reserve the footnotes panel so a scrolling
      // single-section grid does not render under it. Clamped to 0 (never emit a negative CSS height).
      const footnotesAware = Math.max(0, regionHeight - footnotesSectionHeight);
      setMaxGridHeight(footnotesAware);
    }
  }, [footnotesSectionHeight]);

  /**
   * Callback ref to track the Grid Layout container element and calculate its top position
   */
  const gridLayoutCallbackRef = useCallback(
    (gridLayoutElement: HTMLDivElement | null) => {
      // Pass so Footnotes Section useResizeObserver can track it (above)
      containerRef(gridLayoutElement);
      // Ensure our ref is in sync with container node
      gridLayoutNodeRef.current = gridLayoutElement;
      calculateGridHeight();
    },
    [containerRef, calculateGridHeight],
  );

  /**
   * Recalculates grid height on window resize and when parent element resizes
   * (e.g. User resizes browser, or system alerts appearing/disappearing above the grid).
   */
  useEffect(() => {
    calculateGridHeight();
    window.addEventListener('resize', calculateGridHeight);

    // Observe position changes (e.g. System Alerts appearing above the grid)
    const observer = new ResizeObserver(calculateGridHeight);
    const parent = gridLayoutNodeRef.current?.parentElement;
    if (parent) observer.observe(parent);

    return () => {
      window.removeEventListener('resize', calculateGridHeight);
      observer.disconnect();
    };
  }, [calculateGridHeight, renderAlerts]);

  /**
   * The page's column layout. Every page renders from `getPageLayout` now (Galaxy included), so the
   * fetch runs whenever a page is selected — it is the only source of column structure.
   */
  const { data: headerTreeLayout, isLoading: isHeaderTreeLayoutLoading } = useGetPageLayout({
    pageId: currentGranularityPage?.pk ? entityKeyAsId(currentGranularityPage.pk) : undefined,
    deckCycle: pageLayoutOverrides.deckCycle,
    publishYear: pageLayoutOverrides.publishYear,
    asOfDate: pageLayoutOverrides.asOfDate,
    options: { enabled: !isNil(currentGranularityPage?.pk) },
  });

  /** Sections to render, top-to-bottom. */
  const headerTreeSections = useMemo(
    () => (headerTreeLayout?.sections ?? []).filter((section): section is PageLayoutSection => !isNil(section)),
    [headerTreeLayout],
  );

  // Deckline Grid Styling
  const decklineGridStyling = useDecklineGridStyling({
    pageDecklines,
    searchMatches,
    pageDriverSets: pageDriverSets ?? null,
  });

  // Individual display modes for each dimension (static: was previously toggled via the removed
  // Show/Hide Dimensions header dropdown; the value is still read by generateCellValue).
  const [dimensionDisplayModes] = useState<Record<KpiDimension, CodeOrDescription>>(
    Object.values(KpiDimension).reduce(
      (acc, dimension) => {
        acc[dimension] = segmentsDisplayMode;
        return acc;
      },
      {} as Record<KpiDimension, CodeOrDescription>,
    ),
  );
  useEffect(() => {
    // Performs a cleanup for all section grids
    return () => {
      // Unregister all grids when component unmounts, keyed the same way they register
      forEach(headerTreeSections, (section) => {
        gridManagerRef.current?.unregisterPageGrid(`sectionGroup_${section.sectionId ?? '1'}`);
      });
    };
  }, [headerTreeSections]);

  /**
   * Group Decklines by their mapped page section
   */
  const groupDecklinesBySection = (decklines: DecklineRowData[]) => {
    const decklinesBySection: Record<string, DecklineRowData[]> = {};

    forEach(decklines, (deckline) => {
      const pageSection = `sectionGroup_${deckline.rawDecklineData.page_section || '1'}`;

      if (!decklinesBySection[pageSection]) {
        decklinesBySection[pageSection] = [];
      }
      decklinesBySection[pageSection].push(deckline);
    });

    return decklinesBySection;
  };

  /**
   * Utility function to generate the RevGP Cell Value for a given Deckline Row, based on the RevGP Display Mode
   * @param decklineDrivers - The Deckline Drivers to parse and return the RevGP Cell Value for
   */
  const generateCellValue = useCallback(
    (dimension: KpiDimension, decklineDrivers?: AnyKPIDrivers | null) => {
      const valueParts = [];

      if (decklineDrivers) {
        const config = KPI_DIMENSION_CONFIG_MAP[dimension];
        if (!config?.decklineDrivers) return '';

        const driverValues = decklineDrivers as KpiDriverValues;
        const hierarchyValue = config.decklineDrivers.hierarchyAccessor
          ? driverValues[config.decklineDrivers.hierarchyAccessor]
          : undefined;
        const nameValue = driverValues[config.decklineDrivers.valueAccessor];

        if (!hierarchyValue || !nameValue) return nameValue || '';

        const nodeId = `${hierarchyValue}-${nameValue}`;
        const resolvedNode = resolvedNodes?.[nodeId];

        if (resolvedNode) {
          const displayMode = dimensionDisplayModes[dimension];
          if ([CodeOrDescription.CODE, CodeOrDescription.BOTH].includes(displayMode)) {
            valueParts.push(resolvedNode.attributes.name);
          }
          if ([CodeOrDescription.DESCRIPTION, CodeOrDescription.BOTH].includes(displayMode)) {
            valueParts.push(formatNodeDescription(resolvedNode.attributes.description));
          }
        } else if (nameValue) {
          valueParts.push(nameValue);
        }
      }
      return valueParts.join(' - ');
    },
    [resolvedNodes, dimensionDisplayModes],
  );

  /**
   * Build subtotal map and ranges from decklines data
   */
  const buildSubtotalMaps = useCallback(
    (rowData: DecklineRowData[]) => {
      const subtotalMap: Map<string, DecklineRowData[]> = new Map();
      const subtotalRanges: Map<string, { min: number; max: number }> = new Map();

      // Group by subtotal_parent (include all rows for highlighting)
      rowData.forEach((row) => {
        const subtotalParent = row.rawDecklineData?.subtotal_parent;
        if (subtotalParent) {
          if (subtotalMap.has(subtotalParent)) {
            subtotalMap.get(subtotalParent)!.push(row);
          } else {
            subtotalMap.set(subtotalParent, [row]);
          }
        }
      });

      // Calculate subtotal ranges (only use rows with valid row numbers)
      Array.from(subtotalMap.entries()).forEach(([key, childRows]) => {
        const rowNumbers = childRows.map((row) => Number(row.rowNumber)).filter((n) => !Number.isNaN(n));
        if (rowNumbers.length > 0) {
          subtotalRanges.set(key, {
            min: Math.min(...rowNumbers),
            max: Math.max(...rowNumbers),
          });
        }
      });

      setSubtotalMap(subtotalMap);
      setSubtotalRanges(subtotalRanges);
    },
    [setSubtotalMap, setSubtotalRanges],
  );

  /**
   * Parses through Deckline Row Data received from the Api, for a given Page ID, and formats data
   * to populate the Row items in AGGrid
   * @param decklinesData - The raw Page Deckline data to parse and format
   */
  const createDecklineRowData = useCallback(
    (decklinesData: EnhancedDeckline[]): DecklineRowData[] => {
      if (!decklinesData || decklinesData.length === 0) return []; // As no rows exist

      // Preview Mode (Dev-Admin gated): Single check of localStorage access on whether to generate mock data on the page view
      const isPreviewMode = localStorage.getItem('auriga-preview-mode') === 'true' && !isEmpty(headerTreeSections);

      // Display number to apply to all Rows that are not BLANKLINE rows
      let displayRowNumber = 0;
      // Format raw Decklines data to useable DecklineRowData
      const rowData = decklinesData.map((rawDeckline, idx) => {
        const decklineType = determineDecklineType(rawDeckline);
        const isBlankLine = decklineType === PageSidePanelDropDown.BLANK_LINE;
        const isCalculatedKpi = decklineType === PageSidePanelDropDown.CALCULATED_KPI;
        const isAdjustment = decklineType === PageSidePanelDropDown.ADJUSTMENT;
        const isCalculatedAdjustment = decklineType === PageSidePanelDropDown.CALCULATED_ADJUSTMENT;

        const kpiDecklineDrivers = getEffectiveDrivers(
          rawDeckline?.deckline_drivers,
          readPageDrivers(pageDriverSets, datasetSourceFromDrivers(rawDeckline?.deckline_drivers)),
        );
        if (!isBlankLine) displayRowNumber += 1;

        // For calculated KPIs with a formula, parse it to get actual dimension values
        const parsedFormula =
          isCalculatedKpi && rawDeckline?.kpi_formula && !isEmpty(rawDeckline.kpi_formula)
            ? splitRawFormula(
                rawDeckline.kpi_formula,
                // Calculated-KPI formulas are Galaxy-shaped; use the Galaxy page-driver entry.
                asGalaxyDrivers(readPageDrivers(pageDriverSets, DatasetSource.GALAXY)) ?? undefined,
              )
            : null;

        // Generate all dimension values
        const dimensionValues = Object.values(KpiDimension).reduce(
          (acc, dimension) => {
            if (isAdjustment) {
              acc[dimension] =
                dimension === KpiDimension.REVGP ? '(Adjustment)' : generateCellValue(dimension, kpiDecklineDrivers);
            } else if (isCalculatedAdjustment) {
              acc[dimension] =
                dimension === KpiDimension.REVGP
                  ? '(Calculated Adjustment)'
                  : generateCellValue(dimension, kpiDecklineDrivers);
            } else if (parsedFormula) {
              const valueAccessor = KPI_DIMENSION_CONFIG_MAP[dimension]?.decklineDrivers?.valueAccessor;
              const entry = valueAccessor ? parsedFormula[valueAccessor] : undefined;

              if (entry?.isFormula) {
                acc[dimension] = '(Calculated)';
              } else if (entry?.value) {
                acc[dimension] = entry.value;
              } else {
                acc[dimension] = generateCellValue(dimension, kpiDecklineDrivers);
              }
            } else if (isCalculatedKpi) {
              // Calculated KPI without a parseable formula — show (Calculated) for RevGP, fallback for others
              acc[dimension] =
                dimension === KpiDimension.REVGP ? '(Calculated)' : generateCellValue(dimension, kpiDecklineDrivers);
            } else {
              acc[dimension] = generateCellValue(dimension, kpiDecklineDrivers);
            }
            return acc;
          },
          {} as Record<KpiDimension, string | null>,
        );
        // Preview Mode (Dev-Admin gated): seed deterministic values using the same effective
        // column/row format resolution as the grid and Excel export.
        const previewValues: Record<string, number> = {};
        if (!isBlankLine && isPreviewMode) {
          const sectionId = rawDeckline.page_section || '1';
          const section = headerTreeSections.find((candidate) => (candidate.sectionId ?? '1') === sectionId);
          const columns = (section?.columns ?? []).filter((column): column is ColumnLeaf => !isNil(column));

          columns.forEach((column, columnIndex) => {
            if (!column.excelLetter) return;
            const field = `${column.excelLetter.toLowerCase()}_${selectedGranularity?.toLowerCase()}`;
            const format = resolveCellFormat(
              column.format?.cellDefault,
              rawDeckline.format_cell_default,
              cellFormatOverrideFor(rawDeckline.format_cell_overrides, column.colKey),
            );
            // Deterministic seed based on row and column index — values are stable across re-renders.
            const seed = ((idx + 1) * 7 + (columnIndex + 1) * 13) % 100;

            if (format === CellFormat.PERCENT || format === CellFormat.PERCENT_1 || format === CellFormat.PERCENT_2) {
              previewValues[field] = +((seed / 100) * 0.4 - 0.1).toFixed(4);
            } else if (format === CellFormat.BPS) {
              previewValues[field] = Math.round((seed / 100) * 500 - 100);
            } else {
              // Seeded in the page's own units, so preview reads like the published cell: a MILLIONS
              // page stores raw dollars and the formatter divides, rather than storing millions.
              previewValues[field] =
                Math.round((seed / 100) * 50000 - 5000) * pageScaleDivisor(format, currentGranularityPage?.scale);
            }
          });
        }

        const currentDecklineRowData = {
          id: idx + 1,
          rowNumber: isBlankLine ? '' : displayRowNumber, // Empty for blank lines
          kpi_name: rawDeckline?.kpi_name,
          // Store the original raw Deckline data for reference
          rawDecklineData: rawDeckline,
          ...dimensionValues,
          ...previewValues,
        } as DecklineRowData;

        return currentDecklineRowData;
      });

      // Build subtotal maps using the new function
      buildSubtotalMaps(rowData);
      return rowData;
    },
    [
      buildSubtotalMaps,
      generateCellValue,
      pageDriverSets,
      selectedGranularity,
      headerTreeSections,
      // Preview values are seeded in the page's units, so a scale change must rebuild them.
      currentGranularityPage?.scale,
    ],
  );

  // Determines if the Page is still loading — includes all data dependencies and readiness checks to prevent loader flicker
  const isDataLoading =
    isLoading ||
    isDecklinesLoading ||
    isFootnotesLoading ||
    isNil(currentGranularityPage) ||
    // Pages must not mount a grid before their layout descriptor arrives.
    isHeaderTreeLayoutLoading;
  useRecordLoadComplete(!isDataLoading && !isNil(currentGranularityPage));

  // Forces Page grids to refresh when pageRowData changes, or when search matches change
  useEffect(() => {
    const dataProcessTimeouts: ReturnType<typeof setTimeout>[] = [];
    if (!isSaving && !gridManagerRef?.current?.isDragging?.current) {
      gridManagerRef.current?.executeOnAll((gridApi) => {
        // Add a small delay to ensure data is fully processed
        // eslint-disable-next-line @eslint-react/web-api-no-leaked-timeout -- tracked in dataProcessTimeouts array, cleared in cleanup
        const dataProcessTimeout = setTimeout(() => {
          gridApi?.refreshCells({ force: true });
          gridApi?.redrawRows();
        }, 0); // Setting 0 here pushes the refresh to the next tick of the event cycle
        dataProcessTimeouts.push(dataProcessTimeout);
      });
    }

    return () => {
      for (const timeoutId of dataProcessTimeouts) clearTimeout(timeoutId);
    };
  }, [searchMatches, pageRowData, isSaving]);

  // Refresh headers across all Section grids when segmentsDisplayMode changes
  useEffect(() => {
    gridManagerRef.current?.executeOnAll((gridApi) => {
      gridApi.refreshHeader();
    });
  }, [dimensionDisplayModes]);

  // Update the Deckline row data whenever Page Decklines is updated, and data has loaded
  useEffect(() => {
    if (!isDataLoading && pageDecklines) {
      const newRowData = createDecklineRowData(pageDecklines);
      setPageRowData(newRowData);
    }
  }, [isDataLoading, createDecklineRowData, pageDecklines, resolvedNodes, setPageRowData]);

  /**
   * Unit-of-measure line in the page title stack, driven by the page's declared scale.
   *
   * Only a MILLIONS page is labelled; any other scale renders the line empty rather than claiming a
   * unit the numbers do not carry. This was previously hard-coded to '$ in MM' on every page, which
   * mislabelled ONES pages — headcount and similar are not millions of dollars.
   *
   * Note*: still US-dollar specific. Revisit if a non-USD currency (e.g. Euro) or a further scale
   * (thousands, trillions) is ever needed — that wants a currency on the page, not just a scale.
   */
  const monetaryUnitLabel = currentGranularityPage?.scale === PageScaleValues.MILLIONS ? '$ in MM' : '';

  /**
   * Title line shown above the grid and prepended to the Excel export. Sourced from the Page entity,
   * which models one report header per page. Falls back to the page name rather than the legacy
   * 'Missing Report Header' string.
   */
  const reportHeader = currentGranularityPage?.report_header ?? currentCorePageName ?? '';

  /**
   * Pinned leading columns for a headerTree page: the page title stack, then row number + KPI name.
   *
   * The title stack is THREE separate header bands — report header, page grain, monetary unit —
   * rather than the legacy grid's single cell with embedded newlines. Splitting them is what allows a
   * footnote to be attached to an individual line later (each band renders
   * `FootnoteHeaderRenderer`, so it only needs `footnotes` populating); a merged cell has nowhere to
   * hang one. It also removes the need to fake a merged cell in CSS.
   *
   * Like `coreGridColumnDefs`, this omits any dimension columns: the Show/Hide Dimensions group was
   * removed from both grids (it consumed significant horizontal space and its Galaxy 11-dimension
   * config does not describe HCTR's 6). Per-source driver rendering is owned by Epic H.
   */
  const headerTreeCoreColumnDefs: (ColGroupDef<DecklineRowData> | ColDef<DecklineRowData>)[] = useMemo(() => {
    /** One line of the pinned title stack, footnote-ready. */
    const titleBand = (
      headerName: string,
      headerClass: string,
      children: (ColGroupDef<DecklineRowData> | ColDef<DecklineRowData>)[],
    ): ColGroupDef<DecklineRowData> => ({
      headerName,
      headerClass,
      headerGroupComponent: FootnoteHeaderRenderer,
      // No page-title footnotes are modelled yet; the renderer is wired so attaching them later is
      // a data change rather than a structural one.
      headerGroupComponentParams: { footnotes: null } satisfies FootnoteHeaderParams,
      children,
    });

    const pinnedColumns: ColDef<DecklineRowData>[] = [
      {
        field: 'rowNumber',
        rowDrag: (params) => !params.data?.rawDecklineData?.subtotal_formula,
        headerName: '',
        autoHeight: true,
        minWidth: 80,
        maxWidth: 100,
        pinned: 'left',
        resizable: false,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        suppressSizeToFit: true,
        valueFormatter: (params) => (params.value === '' ? '' : params.value),
      },
      {
        headerName: '',
        initialFlex: 1,
        field: 'kpi_name',
        pinned: 'left',
        resizable: false,
        flex: 1,
        minWidth: 250,
        maxWidth: 350,
        wrapText: true,
        autoHeight: true,
        // Reused as-is: already handles display_name override, indent level and row footnotes.
        cellRenderer: KpiNameCellRenderer,
        cellClass: decklineGridStyling.cellClassHandler((params, defaultColDefClasses) => {
          const newClasses = [...defaultColDefClasses, 'ag-cell-wrapper-full-width'];
          if (params.data?.rawDecklineData?.comments?.length) {
            newClasses.push('has-comments');
          }
          return newClasses;
        }),
      },
    ];

    return [
      titleBand(reportHeader, 'header-tree-report-title', [
        titleBand(selectedGranularity ?? '', 'header-tree-report-grain', [
          titleBand(monetaryUnitLabel, 'header-tree-report-unit', pinnedColumns),
        ]),
      ]),
    ];
  }, [decklineGridStyling, selectedGranularity, monetaryUnitLabel, reportHeader]);

  /**
   * Handler for when a row is dragged and dropped within the AGGrid component.
   * @param event - The RowDragEndEvent from AGGrid
   */
  const handleRowDragStart = useCallback((event: RowDragEnterEvent<DecklineRowData>) => {
    setRowDragStartIndex(event.node.sourceRowIndex);
    gridManagerRef.current?.setDragging(true);
  }, []);

  /**
   * Handler for when a row is dragged and dropped within the AGGrid component.
   * @param event - The RowDragEndEvent from AGGrid
   * @param sectionRowData - The current row data for the section
   */
  const handleRowDragEnd = useCallback(
    (event: RowDragEndEvent<DecklineRowData>, sectionRowData: DecklineRowData[]) => {
      if (isNil(rowDragStartIndex) || isNil(event?.overIndex) || rowDragStartIndex === event.overIndex) {
        gridManagerRef.current?.setDragging(false);
        return;
      }

      const { overIndex } = event;

      // NOTE: The correct afterIndex depends on the direction of the drag/drop event.
      // If we're moving a deckline upwards in the list, we need to subtract one from the `overIndex` from the event.
      const afterIndex = rowDragStartIndex > overIndex ? overIndex - 1 : overIndex;

      const { rawDecklineData: targetDecklineData } = sectionRowData[rowDragStartIndex];
      const targetId = targetDecklineData.metadata.internalId;

      // NOTE: A `null` value here translates to moving the deckline to the top of the list.
      const afterId = afterIndex >= 0 ? sectionRowData[afterIndex].rawDecklineData.metadata.internalId : null;

      // Execute changes when drag is completed
      setTimeout(() => {
        repositionDeckline(targetId, afterId);
        setRowDragStartIndex(null);
        // Select the dragged deckline so that the sidepanel populates.
        chooseDeckline(targetDecklineData);
        gridManagerRef.current?.setDragging(false);
      }, 0);
    },
    [chooseDeckline, repositionDeckline, rowDragStartIndex],
  );

  /**
   * Creates the shared Grid Options for rendering a section AGGrid. Column definitions are supplied
   * by SectionGrid itself (core columns + the section's header-tree columns), so they are not set here.
   */
  const createGridOptions = useCallback(
    (sectionRowData: DecklineRowData[]): GridOptions => ({
      gridId: createId(), // Provide a unique grid ID for each grid we create
      defaultColDef: {
        // Leaf columns must be allowed to grow when a section has few columns; a global maxWidth
        // leaves a blank AG Grid center viewport gutter (e.g. FX Summary's 12 leaves). Pinned core
        // columns set their own explicit bounds in headerTreeCoreColumnDefs.
        minWidth: 100,
        resizable: true,
        wrapHeaderText: true, // Keep text wrapping
        wrapText: false, // Disable text wrapping for all cell content by default
        autoHeight: false, // Disable Auto adjust row height for wrapped cell content by default
        sortable: false,
        cellClass: decklineGridStyling.cellClassHandler(),
      },
      excelStyles: decklineGridStyling.excelStyles,
      animateRows: true, // Allow row animation
      enableBrowserTooltips: false,
      groupHeaderHeight: 60,
      headerHeight: 60,
      rowBuffer: 5, // Reduce row buffer from default of 10, to aid performance
      rowData: sectionRowData, // Sets State Decklines data for the rows
      rowDragManaged: false, // Disabled to allow self-management of row reorder
      rowDragText: (params) => {
        // Show KPI Name column data when dragging
        return params.rowNode?.data?.rawDecklineData?.kpi_name;
      },
      suppressMenuHide: true,
      suppressColumnVirtualisation: false, // Default, but explicitly ensuring column virtualization is allowed
      suppressRowVirtualisation: false, // Default, but explicitly ensuring row virtualization is allowed
      suppressMoveWhenRowDragging: true,
      getRowId: (params: GetRowIdParams) => String(params.data.id), // ID set in createDecklineRowData function
      getRowClass: decklineGridStyling.rowClassHandler,
      onRowDragEnter: handleRowDragStart,
      onRowDragEnd: (event) => handleRowDragEnd(event, sectionRowData),
      onCellFocused: (event) => {
        // Ignore event if `rowIndex` is nil or we're in the process of row dragging (`rowDragStartIndex` is populated, or isDragging for a grid is true)
        if (isNil(event.rowIndex) || !isNil(rowDragStartIndex) || gridManagerRef.current?.isDragging?.current) {
          return;
        }

        // NOTE: Moved `chooseDeckline` out of PageSidePanel and into this AGGRid event:
        // 1. When a user right-clicks for our context menu, this ensures the row below the cursor is selected.
        // 2. Arrow key navigation between rows now works with updating the Side Panel.
        if (
          selectedDeckline?.metadata?.internalId !== sectionRowData[event.rowIndex]?.rawDecklineData.metadata.internalId
        ) {
          chooseDeckline(sectionRowData[event.rowIndex].rawDecklineData);
        }
      },
    }),
    [
      decklineGridStyling,
      handleRowDragStart,
      handleRowDragEnd,
      rowDragStartIndex,
      selectedDeckline?.metadata?.internalId,
      chooseDeckline,
    ],
  );

  /**
   * Handle search across Decklines for matches based on User Search term
   * Searches only across KPI ID and RevGP for a match.
   * @param term - The text to compare against targeted fields.
   */
  const handleSearch = useCallback(
    (term: string) => {
      if (term.length >= MINIMUM_SEARCH_TERM_LENGTH) {
        setIsSearching(true);

        // Match is based off lowercase string comparisons
        const matches = filter(pageRowData, (deckline) => {
          return (
            includes(deckline?.kpi_name?.toLowerCase(), term.toLowerCase()) ||
            includes(deckline?.revgp?.toLowerCase(), term.toLowerCase())
          );
        });

        if (!isEmpty(matches)) {
          const matchInternalIds = matches.map((match) => match.rawDecklineData.metadata.internalId);
          setSearchMatches(new Set(matchInternalIds));
        } else {
          // Clear State when no matches exist
          setSearchMatches(new Set());
        }

        // End search
        setIsSearching(false);
      }
    },
    [pageRowData],
  );

  /**
   * Handles search term input changes - called immediately on each keystroke
   * @param value - The changing search text to be updated in State when it meets minimum length requirements
   */
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);

    if (value.length < MINIMUM_SEARCH_TERM_LENGTH) {
      setSearchMatches(new Set());
      setIsSearching(false);
    }
  }, []);

  /**
   * Resets the Search state as needed
   */
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
    setSearchMatches(new Set());
    setIsSearching(false);
  }, []);

  /**
   * Handle saving Page View changes.
   * Resolves pending KPI/Variant creations first (since decklines reference their IDs),
   * then saves footnotes and decklines.
   */
  const handleSaveChanges = useCallback(async () => {
    setIsSaveInProgress(true);
    const { success, error } = await pendingCreations.resolvePendingCreations();
    if (!success) {
      setIsSaveInProgress(false);
      toastNotification({
        visible: true,
        message: `Failed to save new KPIs: ${error?.message ?? 'Unknown error'}`,
        type: 'error',
      });
      return;
    }

    // Success Handler for Save Action.
    const successHandler = () => {
      setIsSaveInProgress(false);
      recordSaveDuration({
        componentId: currentGranularityPage?.pk,
        component: currentCorePageName ?? undefined,
      });
      toastNotification({
        visible: true,
        message: 'Page and Deckline changes saved successfully.',
        type: 'success',
      });
    };

    // Error Handler for Save Action.
    // NOTE: DecklineContext handles most logic internally - this handler only adds component-specific requirements.
    const errorHandler = () => {
      setIsSaveInProgress(false);
    };

    commentsDrawer?.onClose();
    handleSave(successHandler, errorHandler);
  }, [
    pendingCreations,
    commentsDrawer,
    handleSave,
    toastNotification,
    recordSaveDuration,
    currentGranularityPage?.pk,
    currentCorePageName,
  ]);

  // To determine the right click menu options and actions for when no rows are shown
  // True if no rows yet exist, or if all existing rows are deleted
  const isFirstRowAdd = useMemo(
    () => pageDecklines.length === 0 || every(pageDecklines, (deckline) => deckline.metadata.isDeleted),
    [pageDecklines],
  );

  /**
   * Handles context menu actions for the right-clicked row. The row is passed in by the grid rather
   * than read from `selectedDeckline`: AG Grid builds the menu at right-click time, before the
   * selection change from that same click has re-rendered, so `selectedDeckline` could still be the
   * previously selected row.
   * @param actionValue - The context menu action value (e.g., 'Insert Row Below', 'Insert Row Above')
   * @param targetDeckline - The right-clicked deckline, or null when no row was right-clicked
   */
  const contextMenuHandler = useCallback(
    (actionValue: string, targetDeckline: EnhancedDeckline | null) => {
      if (targetDeckline) {
        // Make the right-clicked row the selection (a no-op when it already is): the Footnotes modal and
        // Display Name dialog render from `selectedDeckline`. Dialog/drawer openers are also passed
        // `targetDeckline` directly, as this handler's `selectedDeckline` may still be the previous row.
        chooseDeckline(targetDeckline);
        // Uses internal ID in metadata for tracking lookup reference
        const targetDecklineId = targetDeckline.metadata.internalId;

        switch (actionValue) {
          case ContextMenuEnums.INSERT_ROW_BELOW:
            insertPageDeckline(targetDecklineId, NewDecklinePosition.BELOW);
            break;
          case ContextMenuEnums.INSERT_ROW_ABOVE:
            insertPageDeckline(targetDecklineId, NewDecklinePosition.ABOVE);
            break;
          case ContextMenuEnums.FOOTNOTES:
            setDisplayFootnotesModal(true);
            break;
          case ContextMenuEnums.DECKLINE_HISTORY:
            // Opens Deckline-specific Audit History dialog via search params
            setSearchParams({ history: 'deckline', decklineId: targetDeckline.deckline_id! });
            break;
          case ContextMenuEnums.COMMENT:
            commentsDrawer.openDrawer(targetDeckline);
            break;
          case ContextMenuEnums.DISPLAY_NAME_OVERRIDE:
            displayNameDialog.openDialog(targetDeckline);
            break;
          case ContextMenuEnums.DELETE_DECKLINE:
            deletePageDeckline(targetDecklineId);
            break;
          case ContextMenuEnums.DEBUG_OUTPUT_DECKLINE:
            console.debug('[DEBUG] Deckline:', targetDeckline);
            break;
          case ContextMenuEnums.DEBUG_OUTPUT_CALC_KPI_IDS:
            console.debug('[DEBUG] Calc per-column IDs:', targetDeckline.kpi_id_by_override);
            break;
          default:
            break;
        }
      } else if (isFirstRowAdd && actionValue === ContextMenuEnums.INSERT_ROW) {
        // Handle no rows exist on new page creation
        insertPageDeckline();
      }
    },
    [
      chooseDeckline,
      isFirstRowAdd,
      insertPageDeckline,
      setDisplayFootnotesModal,
      setSearchParams,
      deletePageDeckline,
      commentsDrawer,
      displayNameDialog,
    ],
  );

  /**
   * For tracking whether the Page in View is a single or multi-section Page.
   */
  const isMultiSectionPage = useMemo(() => headerTreeSections.length > 1, [headerTreeSections]);

  /**
   * Builds the right-click context menu options for the right-clicked deckline (see `contextMenuHandler`
   * for why this is the right-clicked row rather than `selectedDeckline`). Dev admin options (the
   * "Admin Dev Tools" submenu) are only built for DEV_TEAM_ADMIN users.
   * @param targetDeckline - The right-clicked deckline, or null when no row was right-clicked
   */
  const getContextMenuOptions = useCallback(
    (targetDeckline: EnhancedDeckline | null): DecklineContextMenuOptions => ({
      options: pageDecklineContextMenuOptions(isFirstRowAdd, targetDeckline, pageLockStatus.isEditingDisabled),
      devAdminOptions: pageDecklineDevAdminContextMenuOptions(isDevTeamAdmin, targetDeckline),
    }),
    [isFirstRowAdd, pageLockStatus.isEditingDisabled, isDevTeamAdmin],
  );

  /**
   * Renders one SectionGrid per `PageLayoutSection`, stacked top-to-bottom in `sections[]` order.
   * Every page renders through this one path — SectionGrid builds its columns from the section's
   * header tree via `useHeaderTreeColumns`.
   *
   * Row/section join: `PageLayoutSection.sectionId` matches `Deckline.page_section` (verified against
   * Opex Fixed's sections '1'..'4' lining up with its decklines' page_section '1'..'4'). A null
   * sectionId means a single-section page, which falls back to '1' — the same convention
   * `groupDecklinesBySection` uses.
   */
  const renderPageGrids = useCallback(() => {
    if (isEmpty(headerTreeSections) || !pageRowData || !gridManagerRef.current) return null;

    // Stale-data guard: while the user navigates between pages, pageRowData can briefly hold the
    // previous page's decklines. Suppress rendering until the row data belongs to the current page
    // (a nil page_name means initial load / freshly inserted rows, which is allowed).
    const dataPageName = pageRowData[0]?.rawDecklineData?.page_name;
    const isCorrectPageData = isNil(dataPageName) || dataPageName === currentCorePageName;
    if (!isCorrectPageData) return null;

    const decklinesBySection = groupDecklinesBySection(pageRowData);

    return (
      <>
        {headerTreeSections.map((section, index) => {
          const sectionLookupKey = `sectionGroup_${section.sectionId ?? '1'}`;
          const sectionDecklines = decklinesBySection[sectionLookupKey] || [];

          return (
            <Box key={`${currentCorePageName}-${sectionLookupKey}-${index}`} className='section-grid-wrapper'>
              <SectionGrid
                section={section}
                sectionLookupKey={sectionLookupKey}
                grain={selectedGranularity ?? ''}
                sectionRowData={sectionDecklines}
                coreColumnDefs={headerTreeCoreColumnDefs}
                sectionGridOptions={createGridOptions(sectionDecklines)}
                getContextMenuOptions={getContextMenuOptions}
                contextMenuHandler={contextMenuHandler}
                gridManager={gridManagerRef.current!}
                isMultiSectionPage={isMultiSectionPage}
                availableHeight={gridScrollRegionHeight}
              />
            </Box>
          );
        })}
      </>
    );
  }, [
    headerTreeSections,
    pageRowData,
    currentCorePageName,
    selectedGranularity,
    headerTreeCoreColumnDefs,
    createGridOptions,
    getContextMenuOptions,
    contextMenuHandler,
    isMultiSectionPage,
    gridScrollRegionHeight,
  ]);

  /**
   * Returns the per-section block column counts, used for Excel Export spacer column insertion.
   * @returns One array of leaf-columns-per-block per section (e.g. [[5, 5, 4, 5], [5, 5, 4]])
   */
  const getBlockSizes = useCallback((): number[][] => {
    // Each section can have a DIFFERENT block structure (e.g. Total Summary: §1 [5,5,4,5], §2 [5,5,4]),
    // so return one block-leaf-count array PER section rather than assuming a shared layout. Descends
    // GROUP -> BLOCK so each entry is leaf-columns-per-block (what the export spacer logic expects),
    // not blocks-per-group. See getSectionBlockLeafCounts.
    return headerTreeSections.map(getSectionBlockLeafCounts);
  }, [headerTreeSections]);

  /**
   * Builds Excel numFmt metadata in the same section and numbered-row order as the grid sheets.
   * NONE remains inherit metadata, so resolveCellFormat can use a section column default for legacy rows.
   */
  const getExcelExportFormatSections = useCallback((): ExcelExportFormatSection[] => {
    const rowsBySection = groupDecklinesBySection(pageRowData);

    return headerTreeSections.map((section) => {
      const columns = (section.columns ?? []).filter((column): column is ColumnLeaf => !isNil(column));
      const sectionLookupKey = `sectionGroup_${section.sectionId ?? '1'}`;
      const numberedRows = (rowsBySection[sectionLookupKey] ?? []).filter((row) => typeof row.rowNumber === 'number');

      return {
        columnKeys: columns.map((column) => column.colKey),
        columnDefaults: columns.map((column) => column.format?.cellDefault ?? null),
        rowDefaults: numberedRows.map((row) => row.rawDecklineData.format_cell_default ?? null),
        // Tier 3, keyed by colKey for the export's own lookup. The grid and preview resolve
        // `format_cell_overrides` via cellFormatOverrideFor; the export needs the same overrides as a
        // map, so a row-wide format can be carved back out of individual columns here too.
        cellOverrides: numberedRows.map((row) =>
          Object.fromEntries(
            (row.rawDecklineData.format_cell_overrides ?? [])
              .filter((entry): entry is DecklineCellFormat => !isNil(entry))
              .map((entry) => [entry.colKey, entry.format]),
          ),
        ),
        // The exported workbook is the layout base the report writer fills with RAW values, so the
        // page's unit has to reach the numeric formats as `#,##0,,` rather than scaling any value.
        pageScale: currentGranularityPage?.scale ?? null,
      };
    });
  }, [headerTreeSections, pageRowData, currentGranularityPage]);

  /**
   * Excel column-letter header cells (A, B, C…) for the first section's data columns.
   *
   * The layout's `columns` are already one entry per data column in order, so no dedupe or sort is
   * needed. Columns with no `excelLetter` are skipped rather than emitting a blank cell, which would
   * shift every following letter out of alignment with its column.
   */
  const excelColumnLetterCells = useMemo(
    () =>
      (headerTreeSections[0]?.columns ?? [])
        .filter((column): column is ColumnLeaf => !isNil(column) && !isEmpty(column.excelLetter))
        .map((column) => ({
          data: { value: column.excelLetter as string, type: 'String' as const },
          styleId: 'excel-column-name',
        })),
    [headerTreeSections],
  );

  // Standard loader for Page and Deckline Driver View
  if (isDataLoading || (!isEmpty(pageDecklines) && isEmpty(pageRowData))) {
    return <AurigaLoader loaderText='Loading Page...' />;
  }

  // Render the main grids view once data is no longer loading and is ready
  return (
    <Box className='page-component'>
      <Box className='page-action-controls'>
        <Box className='left-action-controls'>
          {/* Column Visibility Toggles - Dynamic based on available page grains */}
          <Box className='granularity-controls'>
            <ButtonGroup size='medium'>
              {availablePageGrains.map((grain) => (
                <Button
                  key={grain}
                  variant={selectedGranularity === grain ? 'contained' : 'outlined'}
                  onClick={() => setSelectedGranularity(grain)}
                >
                  {grain}
                </Button>
              ))}
            </ButtonGroup>
          </Box>
          <GFTSearchInput
            className='page-search-input'
            placeholder='Search...'
            minSearchLength={MINIMUM_SEARCH_TERM_LENGTH}
            value={searchTerm}
            loading={isSearching}
            onChange={handleSearchChange}
            onSearch={handleSearch}
            onClear={handleClearSearch}
          />
          {searchMatches.size > 0 && (
            <Box className='page-results-text'>
              <Typography variant='body1'>{`${searchMatches.size} results found.`}</Typography>
            </Box>
          )}
        </Box>
        <Box className='right-action-controls'>
          <Tooltip title='Edit Page'>
            <Box>
              <IconButton
                disabled={pageLockStatus.isEditingDisabled || !currentCorePageName}
                size='medium'
                onClick={() =>
                  navigate(
                    `/deck-preparation/decks/${currentDeckId}/page-setup/${encodeURIComponent(currentCorePageName!)}`,
                  )
                }
              >
                <NoteAltIcon />
              </IconButton>
            </Box>
          </Tooltip>
          <Tooltip title='Download'>
            <IconButton
              size='medium'
              onClick={() => {
                // Enabled Download
                if (featureFlags.enableDownloadPage) {
                  const workbookExportName = `${currentCorePageName}_Export_${new Date().toISOString()}`;

                  // Shared export: the page's working state through the shared Excel mapping. Loaded on
                  // demand so the renderer is fetched only when someone exports.
                  if (sharedExcelExport.isOn) {
                    // Gathering the data runs inside the chain, so a failure there also shows the error toast.
                    import('@amzn/gft-auriga-excel-mapping')
                      .then(({ renderAurigaPage }) =>
                        renderAurigaPage(
                          toAurigaPageData({
                            page: currentGranularityPage,
                            corePageName: currentCorePageName,
                            sections: headerTreeSections,
                            decklines: pageDecklines,
                            footnotes: pageFootnotes,
                            rowData: pageRowData,
                            granularity: selectedGranularity,
                          }),
                        ),
                      )
                      .then((bytes) => {
                        downloadBlob(new Blob([bytes], { type: XLSX_MIME_TYPE }), `${workbookExportName}.xlsx`);
                        toastNotification({
                          visible: true,
                          // TODO(full deploy): drop "(shared export)" once the shared export is the only path.
                          message: 'Excel export generated (shared export). Check downloads folder.',
                          type: 'info',
                        });
                      })
                      .catch((error) => {
                        console.error('[Export] Shared Excel export failed:', error);
                        toastNotification({
                          visible: true,
                          message: 'Excel export failed. Please try again.',
                          type: 'error',
                        });
                      });
                    return;
                  }

                  // To capture individual output of AGGrid supported 'getSheetDataForExcel' command
                  const sheets: string[] = [];
                  let finalApiReference: GridApi | null = null;

                  gridManagerRef.current?.executeOnAll((gridApi, index, gridCount) => {
                    const columnsToExport = gridApi.getColumns()?.map((col) => col.getColId());

                    // Number of columns to merge across for headers (excludes row number col, KPI name col, and the header cell itself)
                    const mergeAcrossColumns = (columnsToExport?.length || 3) - 3;

                    const sheetData = gridApi.getSheetDataForExcel({
                      sheetName: `${currentCorePageName}-${gridApi.getGridId()}`,
                      columnKeys: columnsToExport,
                      prependContent: [
                        {
                          cells: [], // Add a blank row to exported data to match Executive Deck
                        },
                        {
                          cells: [
                            {
                              // Granularity label only appears on the first section header
                              data: {
                                value: index === 0 ? `${selectedGranularity}` : '',
                                type: 'String',
                              },
                              mergeAcross: 1, // Merges 2 columns
                              styleId: 'excel-report-granularity',
                            },
                            {
                              // Report header only appears on the first section
                              data: {
                                value: index === 0 ? reportHeader : '',
                                type: 'String',
                              },
                              mergeAcross: mergeAcrossColumns,
                              styleId: 'excel-report-header',
                            },
                          ],
                        },
                        {
                          cells: [
                            {
                              data: {
                                value: '',
                                type: 'String',
                              },
                            },
                            {
                              data: {
                                value: '',
                                type: 'String',
                              },
                            },
                            // TODO: Some post processing outside of AGGrid will need to occur, as AGGrid excel export does not have a 'mergeDown' to merge cells vertically
                            {
                              // Monetary unit only appears on the first section
                              data: {
                                value: index === 0 ? monetaryUnitLabel : '',
                                type: 'String',
                              },
                              mergeAcross: mergeAcrossColumns,
                              styleId: 'excel-report-monetary-unit',
                            },
                          ],
                        },
                        {
                          // Export column letter identifiers row (A, B, C...) - only appears on the first section
                          cells: [
                            { data: { value: '', type: 'String' } },
                            { data: { value: '', type: 'String' } },
                            ...(index === 0 ? excelColumnLetterCells : []),
                          ],
                        },
                      ],
                      // Footnotes only appended on the last section grid (appears at bottom of merged sheet)
                      // Footnotes start at column B (1 col earlier than headers at C), so +1 to reach same end column
                      appendContent:
                        index === gridCount - 1 ? getFormattedFootnotesForExport(mergeAcrossColumns + 1) : [],
                      processGroupHeaderCallback: (params) => {
                        const groupHeaderDefinition = params.columnGroup.getColGroupDef();
                        const headerClass = groupHeaderDefinition?.headerClass;
                        // Page-owned title bands are already emitted through prependContent. Leaving them
                        // in the grid header duplicates GMS/MTD/$ in MM and creates a padded export row.
                        if (typeof headerClass === 'string' && EXCEL_PREPEND_TITLE_HEADER_CLASSES.has(headerClass)) {
                          return '';
                        }

                        // Return groupHeader minus any carriage or newline returns
                        const groupHeaderName = params.api.getDisplayNameForColumnGroup(params.columnGroup, null);

                        // Remove the default Report name being exported, since it ie being replaced with custom prepended text
                        if (groupHeaderName === `${reportHeader} (${selectedGranularity})\n${monetaryUnitLabel}`)
                          return '';

                        // Block/group bands carry footnote ordinals on the ColGroupDef's
                        // headerGroupComponentParams (set by useHeaderTreeColumns). Append them as Unicode
                        // superscripts so a band footnote (e.g. `% of Revenue¹`) exports like it renders.
                        // Bands without footnotes get an empty suffix.
                        const groupFootnotes = (
                          groupHeaderDefinition?.headerGroupComponentParams as FootnoteHeaderParams | undefined
                        )?.footnotes;
                        const footnoteSuffix = formatFootnoteSuperscripts(groupFootnotes);

                        return `${groupHeaderName.replace(/[\r\n]+/g, ' ').trim()}${footnoteSuffix}`;
                      },
                      processHeaderCallback: (params) => {
                        // Note*: This assumes the final row above the data grid will be a time series. Should this change,
                        // will need to revisit and look to processGroupHeaderCallback for modifications of the time series headers
                        const headerName = params.api.getDisplayNameForColumn(params.column, null);
                        // Leaf columns carry their footnote ordinals on the ColDef's headerComponentParams
                        // (set by useHeaderTreeColumns). Append them as Unicode superscripts so the export
                        // matches the on-screen header. Columns without footnotes get an empty suffix.
                        const headerFootnotes = (
                          params.column.getColDef().headerComponentParams as FootnoteHeaderParams | undefined
                        )?.footnotes;
                        const footnoteSuffix = formatFootnoteSuperscripts(headerFootnotes);
                        if (selectedGranularity === PageGranularity.MTD) {
                          return `${headerName.trim().replace(' ', '-')}${footnoteSuffix}`;
                        }

                        return `${headerName}${footnoteSuffix}`;
                      },
                      processCellCallback: (params) => {
                        // Special handling for the KPI Name column, to format the KPI Name with Footnote Superscript
                        const { column, node, value: kpiNameDefault } = params;
                        if (column.getColId() === 'kpi_name' && node?.data) {
                          const { rawDecklineData } = node.data;
                          const {
                            actual_kpi_name: actualKpiName,
                            display_name: displayName,
                            footnotes,
                            kpi_name_indent_level: indentLevel,
                          } = rawDecklineData;

                          const indentPrefix = indentLevel ? ' '.repeat(indentLevel * KPI_NAME_INDENT_SPACES) : '';
                          // Same precedence as KpiNameCellRenderer: a non-blank override wins.
                          const baseName = displayName?.trim() ? displayName : actualKpiName || kpiNameDefault;

                          if (!footnotes?.length) return `${indentPrefix}${baseName}`;

                          const formattedFootnotes = formatFootnoteSuperscripts(footnotes as number[]);

                          return `${indentPrefix}${baseName}${formattedFootnotes}`;
                        }

                        return kpiNameDefault;
                      },
                    });

                    // Add section sheet data to collection
                    if (sheetData) sheets.push(sheetData);

                    // Track latest reference to a GridApi to be able to export sheets as a workbook
                    finalApiReference = gridApi;
                  });

                  // Export the final Excel workbook via ExcelJS (merges sheets vertically + auto-fits columns)
                  if (!isNil(finalApiReference)) {
                    const gridApi = finalApiReference as GridApi;
                    const blob = gridApi.getMultipleSheetsAsExcel({ data: sheets });
                    if (blob) {
                      const formatSections = getExcelExportFormatSections();

                      processExcelExport(blob, currentCorePageName || 'Export', getBlockSizes(), formatSections)
                        .then((processedBlob) => {
                          downloadBlob(processedBlob, `${workbookExportName}.xlsx`);
                          toastNotification({
                            visible: true,
                            message: 'Excel export generated. Check downloads folder.',
                            type: 'info',
                          });
                        })
                        .catch((error) => {
                          console.error('[Export] Excel post-processing failed:', error);
                          toastNotification({
                            visible: true,
                            message: 'Excel export failed. Please try again.',
                            type: 'error',
                          });
                        });
                    }
                  }
                } else {
                  // Disabled Download
                  console.debug('TODO Download...');
                }
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          {featureFlags.enableAuditHistory && (
            <Button
              className='audit-history-button'
              variant='outlined'
              size='medium'
              startIcon={<AccessTimeIcon />}
              onClick={() => setSearchParams({ history: 'page' })}
            >
              Page History
            </Button>
          )}
          {pageLockStatus.isLocked && pageLockStatus.isLockedByCurrentUser && (
            <AsyncSaveButton
              inProgress={isSaving}
              inProgressContent='Please Wait...'
              buttonProps={{
                className: 'action-button',
                variant: 'contained',
                color: 'primary',
                size: 'medium',
                disabled: isSaving || pageLockStatus.isEditingDisabled,
                onClick: handleSaveChanges,
              }}
            >
              <Typography variant='inherit'>Save Changes</Typography>
            </AsyncSaveButton>
          )}
          {/* Lock/Release Page Button */}
          <LockPageButton
            deckId={currentDeckId ?? undefined}
            pageName={currentCorePageName ?? undefined}
            currentPage={currentGranularityPage}
          />
        </Box>
      </Box>
      <Box
        className='grid-layout'
        ref={gridLayoutCallbackRef}
        // --max-grid-height is only applied to single-section pages; multi-section pages fall back to 80vh default
        style={
          {
            '--footnotes-section-height': `${footnotesSectionHeight}px`,
            ...(maxGridHeight !== null &&
              headerTreeSections.length <= 1 && {
                '--max-grid-height': `${maxGridHeight}px`,
              }),
          } as CSSProperties
        }
      >
        {/* Render Page and Deckline Driver View Grid(s): one SectionGrid per PageLayout section,
            stacked top-to-bottom. Every page renders through this single path. */}
        {!isEmpty(headerTreeSections) && renderPageGrids()}
        {isEmpty(headerTreeSections) && (
          <Typography variant='h6' className='no-data-message'>
            No deck data available
          </Typography>
        )}
      </Box>
      {/* Footnotes Section */}
      {featureFlags.enableFootnotes && !isDataLoading && (
        <FootnotesSection ref={footnotesRef} containerWidth={footnotesContainerWidth} />
      )}
      {featureFlags.enableFootnotes && <FootnotesModal isEditingDisabled={pageLockStatus.isEditingDisabled} />}
      {/* Comments Drawer */}
      <CommentsDrawer {...commentsDrawer} isEditingDisabled={pageLockStatus.isEditingDisabled} />
      {/* Display Name Override Dialog */}
      <DisplayNameDialog {...displayNameDialog} />
      {/* Audit History Dialog (Page and Deckline) */}
      {featureFlags.enableAuditHistory && <AuditHistoryDialog />}
      {/* Saving progress dialog */}
      {/* Dialog is fully controlled by isSaveInProgress — no user dismissal allowed */}
      <Dialog className='saving-dialog' open={isSaveInProgress}>
        <DialogContent>
          <AurigaLoader loaderText='Saving changes...' loaderSize={75} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Page;
